import { useIntl } from 'react-intl';
import {
    BarChart as BarChartComp,
    Bar,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Rectangle,
    Tooltip,
} from 'recharts';

import { BAR_FILLS } from 'src/features/home/types';
import StatisticTooltip from 'src/features/statistic/components/StatisticTooltip';
import { DataKey, TransformedBarItem } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';

type Props = {
    dataKeys: DataKey[];
    data: TransformedBarItem[];
    isLoading: boolean;
};

type CustomShapeProps = {
    dataKeys: DataKey[];
    payload?: TransformedBarItem;
    dataKey: string;
};
const CustomShape = (props: CustomShapeProps) => {
    const { payload, dataKey, dataKeys } = props;
    const lastKey = dataKeys.findLast(
        ({ value }) => payload && Object.keys(payload).includes(value) && !!payload[value],
    )?.value;

    return <Rectangle {...props} radius={lastKey === dataKey ? [6, 6, 0, 0] : undefined} />;
};

export default function BarChart({ dataKeys, data, isLoading }: Props) {
    const { formatMessage } = useIntl();

    if (isLoading) {
        return <Skeleton className="h-90" />;
    }

    if (!data.length) {
        return (
            <GeneralMessageBlock
                icon={ICON_MAP.SEARCH}
                title={formatMessage(statisticMessages.noPaymentsFound)}
                subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
            />
        );
    }

    return (
        <ResponsiveContainer className="min-h-90" width="100%" height="100%">
            <BarChartComp data={data}>
                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                <XAxis dataKey="title" />
                <Legend
                    iconType="circle"
                    formatter={(defaultValue, entry) => {
                        return (
                            <span className="text-gray-900">
                                {dataKeys.find(({ value }) => value === entry.dataKey)?.title ?? defaultValue}
                            </span>
                        );
                    }}
                />
                <YAxis tickCount={10} />
                <Tooltip content={(props) => <StatisticTooltip {...props} />} />
                {dataKeys.map(({ value }, i) => (
                    <Bar
                        key={value}
                        dataKey={value}
                        stackId="statistic"
                        fill={BAR_FILLS[i % BAR_FILLS.length]}
                        shape={(props) => <CustomShape dataKeys={dataKeys} dataKey={value} {...props} />}
                    />
                ))}
            </BarChartComp>
        </ResponsiveContainer>
    );
}
